import * as d3 from 'd3';
import { Dataset, Dimension, PCPDimension, DataItem } from '../types';
import { projectTo1D } from './projection';

// Re-export the projectTo1D function
export { projectTo1D };

/**
 * Create D3 scales for PCP dimensions
 * @param dimensions Array of dataset dimensions
 * @param height Visual height of the PCP
 * @param padding Padding at top and bottom
 * @returns Array of PCP dimensions with scales
 */
export function createPCPScales(dimensions: Dimension[], height: number, padding: number = 30): PCPDimension[] {
  return dimensions.map((dim) => {
    let scale;
    let domain: [number, number] = [0, 1];

    if (dim.type === 'numeric') {
      // For numeric dimensions, use a linear scale
      domain = dim.domain || [0, 1];
      scale = d3
        .scaleLinear()
        .domain(domain)
        .range([height - padding, padding]);
    } else {
      // For categorical dimensions, use a point scale
      const categories = dim.categories || [];
      scale = d3
        .scalePoint()
        .domain(categories)
        .range([height - padding, padding])
        .padding(0.5);

      // For interface consistency, provide a numeric domain
      domain = [0, categories.length - 1];
    }

    return {
      id: dim.id,
      name: dim.name,
      scale,
      domain,
      range: [height - padding, padding],
      type: dim.type,
    };
  });
}

/**
 * Position PCP axes horizontally
 * @param dimensions PCP dimensions
 * @param width Total width available
 * @param padding Padding at left and right
 * @returns New array of dimensions with x positions
 */
export function positionPCPAxes(dimensions: PCPDimension[], width: number, padding: number = 40): PCPDimension[] {
  const xScale = d3
    .scalePoint()
    .domain(dimensions.map((d) => d.id))
    .range([padding, width - padding]);

  return dimensions.map((dim) => ({
    ...dim,
    x: xScale(dim.id) || 0,
  }));
}

/**
 * Create a function to generate PCP polyline paths
 * @param dimensions Array of positioned PCP dimensions
 * @returns Function that generates path strings for data items
 */
export function createPCPPathGenerator(dimensions: PCPDimension[]) {
  return (item: DataItem) => {
    const points = dimensions.map((dim) => {
      const x = dim.x || 0;
      let y;

      if (dim.type === 'numeric') {
        y = dim.scale(item[dim.id]);
      } else {
        y = dim.scale(item[dim.id]);
      }

      return [x, y];
    });

    // Filter out any points with undefined values
    const validPoints = points.filter((p) => !isNaN(p[0]) && !isNaN(p[1]));

    // d3.line generator for the path
    const lineGenerator = d3.line();
    return lineGenerator(validPoints as [number, number][]);
  };
}

/**
 * Generate a color scale based on projected 1D values
 * @param projectedValues Array of 1D projected values
 * @param colorScheme d3 color scheme to use
 * @returns Color scale function
 */
export function createProjectionColorScale(
  projectedValues: number[],
  colorScheme: readonly string[] = d3.schemeBlues[9],
) {
  const extent = d3.extent(projectedValues) as [number, number];
  return d3.scaleSequential().domain(extent).interpolator(d3.interpolateBlues);
}

/**
 * Create a 1D summary visualization data
 * @param dataset The dataset to summarize
 * @param projectedValues Array of projected values
 * @param bins Number of bins for the histogram
 * @returns Array of histogram bins
 */
export function create1DSummary(dataset: Dataset, projectedValues: number[], bins: number = 10) {
  // Create a histogram generator
  const extent = d3.extent(projectedValues) as [number, number];
  const histogram = d3.bin().domain(extent).thresholds(bins);

  // Generate histogram bins
  const histogramData = histogram(projectedValues);

  // Map data items to bins
  const itemToBin = new Map<string, number>();

  histogramData.forEach((bin, i) => {
    const indices = bin.map((value) => projectedValues.indexOf(value));
    indices.forEach((index) => {
      if (index >= 0 && index < dataset.items.length) {
        itemToBin.set(dataset.items[index].id, i);
      }
    });
  });

  return {
    bins: histogramData,
    itemToBin,
  };
}

/**
 * Filter items based on brushed areas in the PCP
 * @param dataset The dataset containing items
 * @param brushes Object mapping dimension IDs to brush extents
 * @returns Array of item IDs that pass all filters
 */
export function filterByBrushes(
  dataset: Dataset,
  brushes: Record<string, { dimension: string; extent: [number, number] }>,
) {
  if (!Object.keys(brushes).length) return dataset.items.map((item) => item.id);

  return dataset.items
    .filter((item) => {
      // Check if the item passes all brushes
      return Object.values(brushes).every((brush) => {
        const { dimension, extent } = brush;
        const dim = dataset.dimensions.find((d) => d.id === dimension);
        if (!dim) return true;

        const value = Number(item[dimension]);
        return value >= extent[0] && value <= extent[1];
      });
    })
    .map((item) => item.id);
}
